import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import dynamicfunction from '../../../utils/dynamicfunction';
import { getContentKeyAccToLang } from '../../../utils/utils';
import {
  getCorpKycAccountOperation,
  getSignatureCard,
  getSignatureCardDropdown,
  saveCorpKycAccountOperationInfo,
  saveSignatureCard,
} from '../../../store/actions/corporateAction';
import { Chips, ErrorText, RowView, Text } from '../components';
import CustomCheckbox from '../../../components/CustomCheckbox';
import { fontSizes } from '../../../constants/constants';
import Button from '../../../components/Button';
import { signature_card_validation_schema } from '../../../validations_schemas/signature_card_schema';
import { useNavigate } from 'react-router';
import CustomInput from '../../../components/CustomInput';
import { logoutUser } from '../../../store/actions/authActions';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const SignatureCard = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isArabic, TextWeight } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const { corporate_post_loading, acc_operation_data, signature_card_dropdown_data } = useSelector((state) => state.corporate);
  const { client_application_id, is_kyc } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_signature_card?.data?.attributes, key, isArabic);
  };

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getSignatureCardDropdown());
    await dispatch(is_kyc ? getCorpKycAccountOperation(client_application_id) : getSignatureCard(client_application_id));
  }

  const onSubmit = (values) => {
    const api_data = {
      corpClientApplicationId: client_application_id,
      isAgreeToTheTermsAndCondition: values?.isAgreeToTheTermsAndCondition,
      accountOperationAuthSigns: [
        {
          corpClientApplicationAccountOperationAuthSignId: 0,
          sequenceId: 1,
          fullName: values?.fullNameOne,
          identificationNo: values?.identificationNoOne,
          accountManagement: values?.accountManagementOne,
          accountManagementOther: values?.accountManagementOtherOne,
        },
        {
          corpClientApplicationAccountOperationAuthSignId: 0,
          sequenceId: 2,
          fullName: values?.fullNameTwo,
          identificationNo: values?.identificationNoTwo,
          accountManagement: values?.accountManagementTwo,
          accountManagementOther: values?.accountManagementOtherTwo,
        },
      ],
    };
    dispatch(
      is_kyc
        ? saveCorpKycAccountOperationInfo(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        })
        : saveSignatureCard(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };
  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? { ...draftValues.current } : {
          isAgreeToTheTermsAndCondition: acc_operation_data?.isAgreeToTheTermsAndCondition,
          accountManagementOne: acc_operation_data?.accountOperationAuthSigns?.[0]?.accountManagement,
          accountManagementOtherOne: acc_operation_data?.accountOperationAuthSigns?.[0]?.accountManagementOther,
          accountManagementTwo: acc_operation_data?.accountOperationAuthSigns?.[1]?.accountManagement ?? '',
          accountManagementOtherTwo: acc_operation_data?.accountOperationAuthSigns?.[1]?.accountManagementOther ?? '',
          fullNameOne: acc_operation_data?.accountOperationAuthSigns?.[0]?.fullName,
          identificationNoOne: acc_operation_data?.accountOperationAuthSigns?.[0]?.identificationNo,
          fullNameTwo: acc_operation_data?.accountOperationAuthSigns?.[1]?.fullName ?? '',
          identificationNoTwo: acc_operation_data?.accountOperationAuthSigns?.[1]?.identificationNo ?? '',
          isSaveExist: false,
        }}
        validationSchema={signature_card_validation_schema}
        onSubmit={(values, functions) => {
          onSubmit(values, functions.resetForm);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, errors, setFieldTouched, touched }) => {
          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className='gap-7 w-full'>
                <CustomCheckbox
                  isChecked={values?.isAgreeToTheTermsAndCondition}
                  label={getContent('i_understand_and_agree')}
                  secondText={getContent('termsand_conditions')}
                  onClick={() => {
                    setFieldValue('isAgreeToTheTermsAndCondition', !values?.isAgreeToTheTermsAndCondition);
                    setFieldTouched('isAgreeToTheTermsAndCondition', false);
                  }}
                  onPressSecondText={() => window.open('/pdfs/terms_and_conitions.pdf', '_blank')}
                />
              </RowView>
              <ErrorText text={getContent(touched?.isAgreeToTheTermsAndCondition ? errors?.isAgreeToTheTermsAndCondition : '')} className='block' />
              <div className='pt-3 sm:pt-5'>
                <Text
                  text={getContent('authorized_signatory_one_heading')}
                  size={fontSizes.h22}
                  isTruncate={false}
                  weight={TextWeight.semiBold}
                  className={'block'}
                />
              </div>
              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('account_management')}
                  value={values?.accountManagementOne}
                  onChange={(e) => {
                    setFieldValue('accountManagementOne', e);
                    setFieldTouched('accountManagementOne', false);
                  }}
                  options={signature_card_dropdown_data?.accountManagement}
                />
                <ErrorText text={getContent(touched?.accountManagementOne ? errors?.accountManagementOne : '')} className='block' />
              </div>
              {Number(values?.accountManagementOne) === 1091 && (
                <div className='pt-3 sm:pt-5'>
                  <CustomInput
                    isRounded
                    isRequired={false}
                    value={values?.accountManagementOtherOne}
                    onChange={(e) => {
                      setFieldValue('accountManagementOtherOne', e.target.value);
                      setFieldTouched('accountManagementOtherOne', true, false);
                    }}
                    placeholder={getContent('please_specify')}
                    errorMessage={getContent(touched?.accountManagementOtherOne ? errors?.accountManagementOtherOne : '')}
                  />
                </div>
              )}
              <RowView className='gap-7 pt-3 sm:pt-5 w-full' isResponsive>
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('full_fame')}
                  value={values?.fullNameOne}
                  onChange={(e) => {
                    setFieldValue('fullNameOne', e.target.value);
                    setFieldTouched('fullNameOne', true, false);
                  }}
                  placeholder={getContent('placeholder_full_name')}
                  errorMessage={getContent(touched?.fullNameOne ? errors?.fullNameOne : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('national_id_passport_no')}
                  value={values?.identificationNoOne}
                  onChange={(e) => {
                    setFieldValue('identificationNoOne', e.target.value);
                    setFieldTouched('identificationNoOne', true, false);
                  }}
                  labelDivClass={"h-9 sm:h-6"}
                  maxLength={15}
                  placeholder={getContent('placeholder_number')}
                  errorMessage={getContent(touched?.identificationNoOne ? errors?.identificationNoOne : '')}
                />
              </RowView>
              <div className='pt-3 sm:pt-5'>
                <Text
                  text={getContent('authorized_signatory_two_heading')}
                  size={fontSizes.h22}
                  isTruncate={false}
                  weight={TextWeight.semiBold}
                  className={'block'}
                />
              </div>
              <div className='pt-3 sm:pt-5'>
                <Chips
                  label={getContent('account_management')}
                  value={values?.accountManagementTwo}
                  onChange={(e) => {
                    setFieldValue('accountManagementTwo', e);
                    setFieldTouched('accountManagementTwo', false);
                  }}
                  required={false}
                  options={signature_card_dropdown_data?.accountManagement}
                />
                <ErrorText text={getContent(touched?.accountManagementTwo ? errors?.accountManagementTwo : '')} className='block' />
              </div>
              {Number(values?.accountManagementTwo) === 1091 && (
                <div className='pt-3 sm:pt-5'>
                  <CustomInput
                    isRounded
                    value={values?.accountManagementOtherTwo}
                    onChange={(e) => {
                      setFieldValue('accountManagementOtherTwo', e.target.value);
                      setFieldTouched('accountManagementOtherTwo', true, false);
                    }}
                    isRequired={false}
                    placeholder={getContent('please_specify')}
                    errorMessage={getContent(touched?.accountManagementOtherTwo ? errors?.accountManagementOtherTwo : '')}
                  />
                </div>
              )}

              <RowView className='gap-7 pt-3 sm:pt-5 w-full' isResponsive>
                <CustomInput
                  isRounded
                  isRequired={false}
                  label={getContent('full_fame')}
                  value={values?.fullNameTwo}
                  onChange={(e) => {
                    setFieldValue('fullNameTwo', e.target.value);
                    setFieldTouched('fullNameTwo', true, false);
                  }}
                  placeholder={getContent('placeholder_full_name')}
                  errorMessage={getContent(touched?.fullNameTwo ? errors?.fullNameTwo : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={false}
                  label={getContent('national_id_passport_no')}
                  value={values?.identificationNoTwo}
                  onChange={(e) => {
                    setFieldValue('identificationNoTwo', e.target.value);
                    setFieldTouched('identificationNoTwo', true, false);
                  }}
                  maxLength={15}
                  labelDivClass={"h-9 sm:h-6"}
                  placeholder={getContent('placeholder_number')}
                  errorMessage={getContent(touched?.identificationNoTwo ? errors?.identificationNoTwo : '')}
                />
              </RowView>
              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  loading={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default SignatureCard;
